import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Search as SearchIcon, 
  FileText, 
  FolderOpen, 
  Package,
  Loader2,
  Sparkles
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Search = () => {
  const { token } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [useAI, setUseAI] = useState(true);
  const [entityTypes, setEntityTypes] = useState(['documents', 'assets', 'projects']);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/search`, {
        query,
        entity_types: entityTypes,
        use_ai: useAI
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleEntityType = (type) => {
    setEntityTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const getIcon = (type) => {
    switch (type) {
      case 'document': return FileText;
      case 'asset': return Package;
      case 'project': return FolderOpen;
      default: return FileText;
    }
  };

  const totalResults = results
    ? (results.documents?.length || 0) + (results.assets?.length || 0) + (results.projects?.length || 0)
    : 0;

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h1 className="page-title">Search</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Search across documents, assets, and projects
        </p>
      </div>

      <div className="p-8">
        {/* Search Form */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search documents, assets, projects..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-12 h-12 text-lg"
                    data-testid="search-input"
                  />
                </div>
                <Button type="submit" disabled={loading || !query.trim()} className="h-12 px-8" data-testid="search-submit">
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Search'
                  )}
                </Button>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-4">
                  <Label className="text-sm text-muted-foreground">Search in:</Label>
                  {['documents', 'assets', 'projects'].map((type) => (
                    <div key={type} className="flex items-center gap-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={entityTypes.includes(type)}
                        onCheckedChange={() => toggleEntityType(type)}
                      />
                      <Label htmlFor={`type-${type}`} className="text-sm capitalize">
                        {type}
                      </Label>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 ml-auto">
                  <Checkbox
                    id="use-ai"
                    checked={useAI}
                    onCheckedChange={(checked) => setUseAI(checked)}
                  />
                  <Label htmlFor="use-ai" className="text-sm flex items-center gap-1">
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                    AI-powered search
                  </Label>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <div className="space-y-6">
            {/* AI Summary */}
            {results.ai_summary && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                    AI Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{results.ai_summary}</p>
                </CardContent>
              </Card>
            )}

            {/* Results Count */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Found {totalResults} result{totalResults !== 1 ? 's' : ''}
              </p>
            </div>

            {totalResults === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <SearchIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No results found for "{query}"</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Documents */}
                {results.documents?.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Documents ({results.documents.length})
                    </h3>
                    <div className="space-y-2">
                      {results.documents.map((doc) => (
                        <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`search-result-doc-${doc.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-muted">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{doc.name}</p>
                                {doc.description && (
                                  <p className="text-sm text-muted-foreground truncate">{doc.description}</p>
                                )}
                              </div>
                              <Badge variant="secondary">{doc.status}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assets */}
                {results.assets?.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Assets ({results.assets.length})
                    </h3>
                    <div className="space-y-2">
                      {results.assets.map((asset) => (
                        <Card key={asset.id} className="hover:shadow-md transition-shadow" data-testid={`search-result-asset-${asset.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-muted">
                                <Package className="w-4 h-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{asset.name}</p>
                                {asset.description && (
                                  <p className="text-sm text-muted-foreground truncate">{asset.description}</p>
                                )}
                              </div>
                              <Badge variant="secondary">{asset.asset_type}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Projects */}
                {results.projects?.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4" />
                      Projects ({results.projects.length})
                    </h3>
                    <div className="space-y-2">
                      {results.projects.map((project) => (
                        <Card key={project.id} className="hover:shadow-md transition-shadow" data-testid={`search-result-project-${project.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-muted">
                                <FolderOpen className="w-4 h-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{project.name}</p>
                                {project.description && (
                                  <p className="text-sm text-muted-foreground truncate">{project.description}</p>
                                )}
                              </div>
                              <Badge variant="secondary">{project.status}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
